import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Vim-style "go to" chords (UGL-105 A0): press `g` then a destination key
 * within a short window to jump. Ships `g u` → the Users roster today; a chord
 * is only added once its destination is a real, reachable route, since one that
 * navigated nowhere would be a stub.
 *
 * Discipline: the leader is ignored while the user is typing (an editable
 * element is focused) or a modifier is held, so it never hijacks input or a
 * real browser shortcut. The pending-leader window is 1s.
 */

const LEADER = 'g';
const WINDOW_MS = 1000;

/** `<leader key>` → path. Extend as sub-surfaces ship. */
const CHORDS: Record<string, string> = {
  u: '/users',
};

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable ||
    target.getAttribute('role') === 'textbox'
  );
}

export function useGoToShortcuts(): void {
  const navigate = useNavigate();

  useEffect(() => {
    let leaderAt = 0;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;

      const key = event.key.toLowerCase();

      // Second key of the chord, still inside the window.
      if (leaderAt !== 0 && event.timeStamp - leaderAt <= WINDOW_MS) {
        leaderAt = 0;
        const path = CHORDS[key];
        if (path !== undefined) {
          event.preventDefault();
          void navigate(path);
        }
        return;
      }

      // Arm the leader.
      leaderAt = key === LEADER ? event.timeStamp : 0;
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [navigate]);
}
